import Phaser from 'phaser';
import type { SceneBeat, SceneWatchedBeat } from '../scene/scene-format';
import { setFlag } from '../game/story-state';
import { VIEW_WIDTH, VIEW_HEIGHT } from '../game/Game';

// EditRunner — the scene's EDIT, running (Order 16.6). A scene is an ordered
// timeline of beats: played (the interactive stage — everything SceneRunner
// already is) and watched (a baked insert — close-up, POV, cutaway — shown
// full-frame over the stage, then cut back). This class owns which beat is
// current, the advance trigger of a played beat, and the whole life of a
// watched beat's insert: the hard cut in, the media, the line, the hard cut out.
//
// The cut is HARD both ways — film grammar's default, and the honest v0. While
// a watched beat holds the frame the simulation is paused by the SceneRunner
// (control, actors, combat, exits all skip); the stage is still there behind
// the insert, exactly as it was, because a cutaway does not move the world.

/** Phaser cache/texture key for a beat's media. Namespaced by scene AND beat so
 *  two locations' inserts can never collide in the caches. */
export const beatMediaKey = (sceneId: string, beatId: string, kind: 'video' | 'image' | 'audio') =>
  `beat:${sceneId}:${beatId}:${kind}`;

/** Queue a scene's watched-beat media. Call from BootScene.preload — the same
 *  loader run that fetches the plate (the scene record names both). */
export function queueBeatMedia(loader: Phaser.Scene, sceneId: string, beats: readonly SceneBeat[]): void {
  for (const b of beats) {
    if (b.kind !== 'watched') continue;
    if (b.image !== undefined) {
      const key = beatMediaKey(sceneId, b.id, 'image');
      if (!loader.textures.exists(key)) loader.load.image(key, b.image);
    }
    if (b.video !== undefined) {
      const key = beatMediaKey(sceneId, b.id, 'video');
      if (!loader.cache.video.has(key)) loader.load.video(key, b.video);
    }
    if (b.audio !== undefined) {
      const key = beatMediaKey(sceneId, b.id, 'audio');
      if (!loader.cache.audio.has(key)) loader.load.audio(key, b.audio);
    }
  }
}

/** Floor on how long a still insert holds when the record doesn't say. */
const DEFAULT_STILL_HOLD = 2.5;
/** Floor on ANY insert, so a mis-authored beat can never strobe a single frame. */
const MIN_HOLD = 0.35;

/** Draw depths for the insert. Above EVERYTHING, including the HUD (1000s) — a
 *  watched beat is a cut, and a cut leaves no interactive chrome in frame. */
const INSERT_MASK_DEPTH = 2000;
const INSERT_DEPTH = 2001;

export class EditRunner {
  private readonly scene: Phaser.Scene;
  private readonly sceneId: string;
  private readonly beats: readonly SceneBeat[];
  private index = 0;

  // The live insert (watched beats only).
  private mask: Phaser.GameObjects.Rectangle | null = null;
  private insert: Phaser.GameObjects.Image | Phaser.GameObjects.Video | null = null;
  private voice: Phaser.Sound.BaseSound | null = null;
  private mediaDone = true;
  private audioDone = true;
  private holdLeft = 0;

  constructor(scene: Phaser.Scene, sceneId: string, beats: readonly SceneBeat[]) {
    this.scene = scene;
    this.sceneId = sceneId;
    this.beats = beats;
    this.enterBeat(0);
  }

  /** True while a watched beat holds the frame — the SceneRunner pauses the
   *  simulation on it. */
  get watching(): boolean {
    return this.beats[this.index]?.kind === 'watched';
  }

  /** The current beat's id, for the HUD/debug. */
  get beatId(): string {
    return this.beats[this.index]?.id ?? '?';
  }

  /** Played-beat frame: leave on TRIGGER — the player stepped into the advance
   *  circle. Call with the player's ground position; no-op on watched beats. */
  updatePlayed(playerX: number, playerZ: number): void {
    const beat = this.beats[this.index];
    if (!beat || beat.kind !== 'played' || !beat.advance) return;
    const a = beat.advance;
    const dx = playerX - a.x;
    const dz = playerZ - a.z;
    if (dx * dx + dz * dz > a.radius * a.radius) return;
    for (const f of a.set ?? []) setFlag(f);
    this.enterBeat(this.index + 1);
  }

  /** Watched-beat frame: run the insert's clocks, leave on COMPLETION — video
   *  ended, line ended, hold elapsed, whichever is latest. */
  updateWatched(dtSec: number): void {
    const beat = this.beats[this.index];
    if (!beat || beat.kind !== 'watched') return;
    this.holdLeft -= dtSec;
    // A video's texture frame swaps from a placeholder to the real frame a few
    // ticks in, which invalidates any scale set before the swap — so a video
    // insert re-fits EVERY frame. setDisplaySize resolves against the current
    // frame, so once the real one is in, this is a fixed point (and it is a
    // couple of multiplies, not a layout).
    if (this.insert && this.insert instanceof Phaser.GameObjects.Video) {
      const v = this.insert.video;
      if (v && v.videoWidth > 0) this.fitToView(this.insert, v.videoWidth / v.videoHeight);
    }
    if (this.mediaDone && this.audioDone && this.holdLeft <= 0) {
      this.endInsert();
      this.enterBeat(this.index + 1);
    }
  }

  destroy(): void {
    this.endInsert();
  }

  private enterBeat(i: number): void {
    // Past the end can't happen on valid data (the validator pins the last beat
    // to 'played', which only leaves through exits) — clamp anyway.
    this.index = Math.min(i, this.beats.length - 1);
    const beat = this.beats[this.index];
    if (beat.kind === 'watched') this.showInsert(beat);
  }

  /** The hard cut in: a full-view black mask, the insert letterboxed over it,
   *  the line landing with it. All screen-fixed — an insert is a CUT, it does
   *  not live in the world. */
  private showInsert(beat: SceneWatchedBeat): void {
    this.mask = this.scene.add.rectangle(0, 0, VIEW_WIDTH, VIEW_HEIGHT, 0x000000, 1)
      .setOrigin(0, 0).setScrollFactor(0).setDepth(INSERT_MASK_DEPTH);

    if (beat.image !== undefined) {
      const img = this.scene.add.image(VIEW_WIDTH / 2, VIEW_HEIGHT / 2, beatMediaKey(this.sceneId, beat.id, 'image'))
        .setScrollFactor(0).setDepth(INSERT_DEPTH);
      this.fitToView(img, img.width / img.height);
      this.insert = img;
      this.mediaDone = true;
    } else {
      const vid = this.scene.add.video(VIEW_WIDTH / 2, VIEW_HEIGHT / 2, beatMediaKey(this.sceneId, beat.id, 'video'))
        .setScrollFactor(0).setDepth(INSERT_DEPTH);
      this.insert = vid;
      this.mediaDone = false;
      vid.once(Phaser.GameObjects.Events.VIDEO_COMPLETE, () => { this.mediaDone = true; });
      // A video that CAN'T run (browser blocks unmuted autoplay pre-gesture, or
      // decode fails) must not strand the whole edit on a black frame: the beat
      // falls back to its hold and the scene cuts on.
      vid.once(Phaser.GameObjects.Events.VIDEO_ERROR, () => { this.mediaDone = true; });
      vid.once(Phaser.GameObjects.Events.VIDEO_UNSUPPORTED, () => { this.mediaDone = true; });
      vid.play(false);
    }

    if (beat.audio !== undefined) {
      this.audioDone = false;
      this.voice = this.scene.sound.add(beatMediaKey(this.sceneId, beat.id, 'audio'));
      this.voice.once(Phaser.Sound.Events.COMPLETE, () => { this.audioDone = true; });
      this.voice.play();
    } else {
      this.audioDone = true;
      this.voice = null;
    }

    const hold = beat.holdSeconds ?? (beat.image !== undefined && beat.audio === undefined ? DEFAULT_STILL_HOLD : 0);
    this.holdLeft = Math.max(hold, MIN_HOLD);
  }

  /** Letterbox media into the view: fit the width, centre vertically, never
   *  distort — the same rule the fixed camera applies to the plate. */
  private fitToView(obj: Phaser.GameObjects.Image | Phaser.GameObjects.Video, aspect: number): void {
    let dw = VIEW_WIDTH;
    let dh = dw / aspect;
    if (dh > VIEW_HEIGHT) { dh = VIEW_HEIGHT; dw = dh * aspect; }
    obj.setDisplaySize(dw, dh);
  }

  /** The hard cut out. */
  private endInsert(): void {
    this.insert?.destroy();
    this.mask?.destroy();
    this.voice?.stop();
    this.voice?.destroy();
    this.insert = null;
    this.mask = null;
    this.voice = null;
  }
}
